import { useEffect, useRef } from "react";
import { GroupBase } from "react-select";
import type { UseFormReturn } from "react-hook-form";

import { ICreateCompanyForm } from "../CompanyForm";
import { ICompany, ISuggestionCompany } from "@interfaces/company.interface";
import { UserForDropdown } from "@app-types/common.type";

import { CompanyTypeSubtype } from "@enums/companies.enum";
import { ICompanyTypeSelectOption, IPrioritySelectOption } from "../Selects";
import { unassignedOption } from "../Selects/PlatformCompanySelect";
import { IPipelineStageSelectOption } from "@components/AddNewEntity/Shared/Selects";

interface UseCompanyFormInitializationProps {
  form: Pick<UseFormReturn<ICreateCompanyForm>, "reset" | "setValue" | "getValues">;
  query?: string;
  company?: ICompany;
  matchedCompany?: ISuggestionCompany | null;
  companyTypesOptions: GroupBase<ICompanyTypeSelectOption>[];
  prioritiesOptions: IPrioritySelectOption[];
  stageOptions: GroupBase<IPipelineStageSelectOption>[];
  userOptions: UserForDropdown[];
  onUpdateCompanyFromBrandfetch?: (company: ISuggestionCompany) => void;
}

export function useCompanyFormInitialization({
  form,
  query,
  company,
  matchedCompany,
  companyTypesOptions,
  prioritiesOptions,
  stageOptions,
  userOptions,
  onUpdateCompanyFromBrandfetch,
}: UseCompanyFormInitializationProps) {
  const isExistingCompany = Boolean(company?.id);
  const shouldDirty = !isExistingCompany;
  const { reset, setValue, getValues } = form;

  // 1. Title, Website & Familiar name for an existing company — seed once, then the
  // form owns them so a late inline-save response can't overwrite unblurred edits.
  const editFieldsInitializedRef = useRef(false);
  useEffect(() => {
    if (!isExistingCompany || editFieldsInitializedRef.current) return;
    reset(
      {
        ...getValues(),
        title: company.title,
        website: company.website,
        familiar_name: company.familiar_name ?? "",
      },
      { keepDirty: false }
    );
    editFieldsInitializedRef.current = true;
  }, [
    isExistingCompany,
    company?.title,
    company?.website,
    company?.familiar_name,
    reset,
    getValues,
  ]);

  // 2. Title for new company from search query
  useEffect(() => {
    if (query && !isExistingCompany) {
      setValue("title", query, { shouldDirty: true });
    }
  }, [query]);

  // 3. Brandfetch suggestions for new company
  useEffect(() => {
    if (!matchedCompany || isExistingCompany) return;
    setValue("title", matchedCompany.name, {
      shouldValidate: true,
      shouldDirty,
      shouldTouch: true,
    });
    onUpdateCompanyFromBrandfetch?.(matchedCompany);
  }, [matchedCompany?.name, isExistingCompany, setValue, onUpdateCompanyFromBrandfetch]);

  // 4. Type & Platform
  useEffect(() => {
    let defaultType =
      companyTypesOptions[0]?.options.find((o) => o.is_default) ||
      companyTypesOptions[0]?.options[0] ||
      null;

    if (isExistingCompany && company?.type) {
      defaultType = {
        ...company.type,
        label: company.type.title,
        value: company.type.id,
      };
    }

    setValue("type", defaultType, { shouldDirty });

    // 5. Platform for ADD_ON type — default to "No company" (unassignedOption)
    if (defaultType?.subtype === CompanyTypeSubtype.ADD_ON) {
      if (isExistingCompany && company?.platform_company) {
        const currentPlatform = {
          company: company.platform_company,
          label: company.platform_company.title,
          value: company.platform_company.id,
        };
        setValue("platform", currentPlatform, { shouldDirty });
      } else {
        setValue("platform", unassignedOption, { shouldDirty });
      }
    } else {
      setValue("platform", null, { shouldDirty });
    }
  }, [
    companyTypesOptions,
    company?.type?.id,
    company?.platform_company?.id,
    isExistingCompany,
    setValue,
  ]);

  // 6. Priority
  useEffect(() => {
    let defaultPriority =
      prioritiesOptions.find((p) => p.is_default) || prioritiesOptions[0] || null;

    if (isExistingCompany && company?.priority) {
      defaultPriority = {
        ...company.priority,
        label: company.priority.title,
        value: company.priority.id,
      };
    }

    setValue("priority", defaultPriority, { shouldDirty });
  }, [prioritiesOptions, company?.priority?.id, isExistingCompany, setValue]);

  // 7. Stage
  useEffect(() => {
    let defaultStage =
      stageOptions[1]?.options.find((o) => o.is_default) ||
      stageOptions[1]?.options[0] ||
      stageOptions[0]?.options[0] ||
      null;

    if (company?.stage_object_id) {
      defaultStage = stageOptions[1]?.options.find((o) => o.id === company.stage_object_id) || null;
    } else if (isExistingCompany) {
      defaultStage = stageOptions[0]?.options[0] || null;
    }

    setValue("stage", defaultStage as IPipelineStageSelectOption | null, {
      shouldDirty,
    });
  }, [stageOptions, company?.stage_object_id, isExistingCompany, setValue]);

  // 8. Users — only initialize once; after that the form owns the state
  // and inline saves keep the server in sync without resetting local edits.
  const usersInitializedRef = useRef(false);

  useEffect(() => {
    if (!userOptions.length) return;
    if (usersInitializedRef.current) return;

    const defaultUsers = isExistingCompany
      ? userOptions.filter((user) => company?.users.includes(user.id))
      : userOptions[0]
        ? [userOptions[0]]
        : [];

    reset({ ...getValues(), users: defaultUsers }, { keepDirty: shouldDirty });
    usersInitializedRef.current = true;
  }, [userOptions, company?.users, isExistingCompany, reset, getValues]);
}
